import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Flex,
  Text,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Button,
  Input,
  Select,
  SimpleGrid,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  useToast,
  useColorModeValue,
  useBreakpointValue,
  Badge,
  Spinner,
  Switch,
} from "@chakra-ui/react";
import { MdEdit, MdDelete, MdAdd } from "react-icons/md";
import Card from "components/card/Card";
import fetchWithToken from "views/auth/signIn/axiosInstance";
import { useTranslation } from "react-i18next";
import CreateSubCategory from "./CreateSubCategory";
import PAGE_IDS from "constants/pageIds";
import { GOLD_BORDER, GOLD_TEXT, GOLD_LIGHT } from "constants/brandColors";
import { getTranslatedField, translationsArrayToMap, useLocales, validateNameTranslations } from "utils/translations";
import LocaleNameTabs from "components/localeNameFields/LocaleNameTabs";
import TranslationsCell from "components/localeNameFields/TranslationsCell";

const SUBCATEGORIES_PAGE_ID = PAGE_IDS.SUBCATEGORIES;

const fieldProps = {
  bg: "white",
  borderColor: GOLD_BORDER,
  focusBorderColor: GOLD_BORDER,
};

const emptyNameTranslation = () => ({ name: "" });

const ListSubCategory = () => {
  const { t } = useTranslation();
  const { locales } = useLocales();
  const [subcategories, setSubcategories] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [editableSubcategory, setEditableSubcategory] = useState(null);
  const [editErrors, setEditErrors] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const toast = useToast();
  const modalSize = useBreakpointValue({ base: "full", md: "md" });
  const tableHeaderBg = useColorModeValue("gray.50", "gray.700");
  const tableHeaderColor = useColorModeValue("gray.600", "gray.200");
  const rowHoverBg = useColorModeValue("gray.50", "whiteAlpha.100");

  const [permissions, setPermissions] = useState({ canList: true, canEdit: true, canDelete: true, canInsert: true });

  useEffect(() => {
    try {
      const role = JSON.parse(sessionStorage.getItem("permissions") || "{}");
      if (!role.permissions) return;
      const permission = role.permissions.find((p) => p.page_id === SUBCATEGORIES_PAGE_ID);
      if (permission) {
        setPermissions({
          canList: permission.read_all_access || false,
          canEdit: permission.update_access || false,
          canDelete: permission.delete_access || false,
          canInsert: permission.insert_access || false,
        });
      }
    } catch (error) {
      // ignore malformed permissions blob
    }
  }, []);

  const fetchCategories = useCallback(async () => {
    try {
      const response = await fetchWithToken(`${process.env.REACT_APP_NEWS_API_URL}/category`);
      setCategories(Array.isArray(response) ? response : []);
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  }, []);

  const fetchSubcategories = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetchWithToken(`${process.env.REACT_APP_NEWS_API_URL}/sub/category`);
      setSubcategories(Array.isArray(response) ? response : []);
    } catch (error) {
      toast({
        title: t("An error occurred."),
        description: error.message || t("Failed to fetch sub categories."),
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top-right",
      });
    } finally {
      setLoading(false);
    }
  }, [t, toast]);

  useEffect(() => {
    fetchCategories();
    fetchSubcategories();
  }, [fetchCategories, fetchSubcategories]);

  const categoryName = (categoryId) => {
    const category = categories.find((c) => c.id === categoryId);
    return category ? getTranslatedField(category.translations, "ar") : "—";
  };

  const handleCreated = () => {
    setShowCreateModal(false);
    fetchSubcategories();
  };

  const handleEdit = (subcategory) => {
    setEditErrors({});
    setEditableSubcategory({
      ...subcategory,
      translations: translationsArrayToMap(subcategory.translations, emptyNameTranslation, locales),
      slug: subcategory.slug || "",
      order: subcategory.order ?? "",
    });
  };

  const setEditTranslationField = (code, value) => {
    setEditableSubcategory((prev) => ({
      ...prev,
      translations: { ...prev.translations, [code]: { ...emptyNameTranslation(), ...prev.translations[code], name: value } },
    }));
  };

  const handleSave = async () => {
    const errors = validateNameTranslations(editableSubcategory.translations, locales, t);
    setEditErrors(errors);
    if (Object.keys(errors).length > 0) {
      return;
    }

    setIsSaving(true);
    try {
      const translationsPayload = locales
        .map(({ code }) => ({ locale: code, name: (editableSubcategory.translations[code]?.name || "").trim() }))
        .filter((tr) => tr.name);

      const data = {
        page_id: SUBCATEGORIES_PAGE_ID,
        subcategoryId: editableSubcategory.id,
        translations: translationsPayload,
        categoryId: Number(editableSubcategory.categoryId),
        slug: editableSubcategory.slug.trim(),
        ...(editableSubcategory.order !== "" && { order: Number(editableSubcategory.order) }),
      };
      const response = await fetchWithToken(
        `${process.env.REACT_APP_NEWS_API_URL}/sub/category`,
        "PUT",
        data
      );
      if (!response) {
        throw new Error("Failed to update sub category");
      }
      await fetchSubcategories();
      setEditableSubcategory(null);
      toast({
        title: t("Sub category updated successfully."),
        status: "success",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });
    } catch (error) {
      toast({
        title: t("An error occurred."),
        description: error.message || t("Failed to update sub category."),
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top-right",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (subcategoryId) => {
    try {
      const response = await fetchWithToken(
        `${process.env.REACT_APP_NEWS_API_URL}/sub/category`,
        "DELETE",
        { page_id: SUBCATEGORIES_PAGE_ID, subcategoryId }
      );
      if (!response) {
        throw new Error("Failed to delete sub category");
      }
      setSubcategories((prev) => prev.filter((s) => s.id !== subcategoryId));
      toast({
        title: t("Sub category deleted successfully."),
        status: "success",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });
    } catch (error) {
      // Server returns 400 when a News item still references this subcategory.
      toast({
        title: t("Unable to delete sub category."),
        description: error.message || t("It may still be in use by news."),
        status: "error",
        duration: 6000,
        isClosable: true,
        position: "top-right",
      });
    }
  };

  // Quick one-click on/off toggle right in the table, same pattern as the
  // Languages page's Switch — separate from the full edit modal.
  const handleToggleActive = async (subcategory) => {
    try {
      const response = await fetchWithToken(
        `${process.env.REACT_APP_NEWS_API_URL}/sub/category`,
        "PUT",
        { page_id: SUBCATEGORIES_PAGE_ID, subcategoryId: subcategory.id, isActive: !subcategory.isActive }
      );
      if (!response) {
        throw new Error("Failed to update sub category");
      }
      setSubcategories((prev) => prev.map((s) => (s.id === subcategory.id ? { ...s, isActive: !s.isActive } : s)));
    } catch (error) {
      toast({
        title: t("An error occurred."),
        description: error.message || t("Failed to toggle sub category status."),
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top-right",
      });
    }
  };

  const filteredSubcategories = subcategories.filter((s) => {
    const q = searchQuery.toLowerCase();
    return locales.some(({ code }) => getTranslatedField(s.translations, code).toLowerCase().includes(q));
  });

  return (
    <Card direction="column" w="100%" px="0px" overflowX={{ sm: "scroll", lg: "hidden" }} boxShadow="lg">
      <Flex px={{ base: "10px", md: "25px" }} justify="space-between" mb="20px" align="center" flexWrap="wrap">
        <Flex align="center">
          <Text color="gray.800" fontSize={{ base: "18px", md: "22px" }} fontWeight="700" lineHeight="100%" mb={{ base: "10px", md: "0" }}>
            {t("Sub Categories")}
          </Text>
          <Button
            onClick={() => setShowCreateModal(true)}
            isDisabled={!permissions.canInsert}
            mb={1}
            ml={4}
            bg={GOLD_BORDER}
            color="#1A1A1A"
            _hover={{ bg: GOLD_TEXT, color: "white" }}
            _active={{ bg: GOLD_TEXT, color: "white" }}
            leftIcon={<MdAdd />}
            borderRadius="full"
            boxShadow="sm"
          >
            {t("Add Sub Category")}
          </Button>
        </Flex>
        <Input
          placeholder={t("Search sub category")}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          size="sm"
          w={{ base: "100%", md: "200px" }}
          borderColor={GOLD_BORDER}
          focusBorderColor={GOLD_BORDER}
        />
      </Flex>

      <Box overflowX="auto" overflowY="auto" maxH="60vh" px="0px">
        {loading ? (
          <Flex justify="center" py={10}><Spinner size="xl" /></Flex>
        ) : (
          <Table variant="simple">
            <Thead>
              <Tr bg={tableHeaderBg}>
                <Th color={tableHeaderColor} fontWeight="600">{t("Name")}</Th>
                <Th color={tableHeaderColor} fontWeight="600">{t("Category")}</Th>
                <Th color={tableHeaderColor} fontWeight="600">{t("Status")}</Th>
                <Th color={tableHeaderColor} fontWeight="600">{t("Actions")}</Th>
              </Tr>
            </Thead>
            <Tbody>
              {filteredSubcategories.map((subcategory) => (
                <Tr key={subcategory.id} borderBottom="1px solid" borderColor="gray.100" _hover={{ bg: rowHoverBg }}>
                  <Td>
                    <TranslationsCell translations={subcategory.translations} locales={locales} />
                  </Td>
                  <Td>{categoryName(subcategory.categoryId)}</Td>
                  <Td>
                    <Flex align="center" gap={2}>
                      <Badge colorScheme={subcategory.isActive ? "green" : "gray"} variant="solid" px={2} py={1} borderRadius="md" fontSize="0.85em">
                        {subcategory.isActive ? t("Active") : t("Inactive")}
                      </Badge>
                      <Switch
                        size="sm"
                        isChecked={subcategory.isActive}
                        isDisabled={!permissions.canEdit}
                        onChange={() => handleToggleActive(subcategory)}
                        colorScheme="brand"
                      />
                    </Flex>
                  </Td>
                  <Td>
                    <Flex>
                      <Button
                        variant="outline"
                        borderColor={GOLD_BORDER}
                        color={GOLD_TEXT}
                        _hover={{ bg: GOLD_LIGHT }}
                        size="sm"
                        mr="2"
                        isDisabled={!permissions.canEdit}
                        onClick={() => handleEdit(subcategory)}
                        leftIcon={<MdEdit />}
                      >
                        {t("Edit")}
                      </Button>
                      <Button
                        colorScheme="red"
                        variant="outline"
                        size="sm"
                        isDisabled={!permissions.canDelete}
                        onClick={() => handleDelete(subcategory.id)}
                        leftIcon={<MdDelete />}
                      >
                        {t("Delete")}
                      </Button>
                    </Flex>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        )}
      </Box>

      {editableSubcategory && (
        <Modal isOpen={true} onClose={() => setEditableSubcategory(null)} size={modalSize}>
          <ModalOverlay />
          <ModalContent>
            <ModalHeader borderBottom="1px solid" borderColor="gray.100">
              <Text color={GOLD_TEXT} fontSize="2xl" fontWeight="700">
                {t("Edit Sub Category")}
              </Text>
            </ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <LocaleNameTabs
                locales={locales}
                translations={editableSubcategory.translations}
                errors={editErrors}
                onChange={setEditTranslationField}
                borderColor={GOLD_BORDER}
              />

              <Box mt="16px">
                <Text fontSize="sm" color="gray.600" mb="2">{t("Category")}</Text>
                <Select
                  value={editableSubcategory.categoryId}
                  onChange={(e) => setEditableSubcategory({ ...editableSubcategory, categoryId: e.target.value })}
                  {...fieldProps}
                >
                  {categories.map((category) => (
                    <option key={category.id} value={category.id}>{getTranslatedField(category.translations, "ar")}</option>
                  ))}
                </Select>
              </Box>

              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} mt="16px">
                <Box>
                  <Text fontSize="sm" color="gray.600" mb="2">{t("Slug")}</Text>
                  <Input
                    value={editableSubcategory.slug}
                    onChange={(e) => setEditableSubcategory({ ...editableSubcategory, slug: e.target.value })}
                    {...fieldProps}
                  />
                </Box>
                <Box>
                  <Text fontSize="sm" color="gray.600" mb="2">{t("Order")}</Text>
                  <Input
                    type="number"
                    value={editableSubcategory.order}
                    onChange={(e) => setEditableSubcategory({ ...editableSubcategory, order: e.target.value })}
                    {...fieldProps}
                  />
                </Box>
              </SimpleGrid>
            </ModalBody>
            <ModalFooter borderTop="1px solid" borderColor="gray.100" gap={3}>
              <Button variant="outline" borderColor="gray.200" color="gray.700" onClick={() => setEditableSubcategory(null)}>
                {t("Cancel")}
              </Button>
              <Button
                bg={GOLD_BORDER}
                color="#1A1A1A"
                _hover={{ bg: GOLD_TEXT, color: "white" }}
                _active={{ bg: GOLD_TEXT, color: "white" }}
                onClick={handleSave}
                isLoading={isSaving}
              >
                {t("Save")}
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      )}

      <Modal isOpen={showCreateModal} onClose={() => setShowCreateModal(false)} size={modalSize}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader borderBottom="1px solid" borderColor="gray.100">
            <Text color={GOLD_TEXT} fontSize="2xl" fontWeight="700">
              {t("Create Sub Category")}
            </Text>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <CreateSubCategory onCreated={handleCreated} />
          </ModalBody>
        </ModalContent>
      </Modal>
    </Card>
  );
};

export default ListSubCategory;
